class RecipeLibrary extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('[data-recipe-search]');
    this.filters = Array.from(this.querySelectorAll('[data-recipe-filter]'));
    this.cards = Array.from(this.querySelectorAll('[data-recipe-card]'));
    this.emptyState = this.querySelector('[data-recipe-empty]');
    this.status = this.querySelector('[data-recipe-status]');
    this.activeCategory = 'all';

    this.searchInput?.addEventListener('input', () => this.update());
    this.filters.forEach((button) => {
      button.addEventListener('click', () => {
        this.activeCategory = button.dataset.recipeFilter || 'all';
        this.filters.forEach((filter) => {
          filter.setAttribute('aria-pressed', String(filter === button));
        });
        this.update();
      });
    });

    this.update();
  }

  update() {
    const query = (this.searchInput?.value || '').trim().toLowerCase();
    let visibleCount = 0;

    this.cards.forEach((card) => {
      const matchesSearch = !query || (card.dataset.recipeSearch || '').includes(query);
      const matchesCategory = this.activeCategory === 'all' || card.dataset.recipeCategory === this.activeCategory;
      const visible = matchesSearch && matchesCategory;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
    if (this.status) {
      const template = visibleCount === 1
        ? this.status.dataset.statusSingle || '[count] recipe'
        : this.status.dataset.statusTemplate || '[count] recipes';
      this.status.textContent = template.replace('[count]', visibleCount);
    }
  }
}

class RecipeDetail extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-recipe-source]');
    if (!this.content) return;
    this.normalizeSource();
    this.prepareHero();
    this.prepareContent();
    this.querySelector('[data-recipe-print]')?.addEventListener('click', () => window.print());
  }

  normalizeSource() {
    const directSemanticContent = Array.from(this.content.children).some((node) => /^(H1|H2|UL|OL)$/.test(node.tagName));
    if (directSemanticContent) return;

    const nodes = Array.from(this.content.querySelectorAll('h1, h2, h3, p, ul, ol, hr, figure'))
      .filter((node) => !node.parentElement?.closest('li'))
      .filter((node) => node.textContent.trim() || node.querySelector('img'));
    if (!nodes.length) return;

    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    this.content.replaceChildren(fragment);
  }

  prepareHero() {
    const title = this.dataset.pageTitle?.trim();
    const heading = Array.from(this.content.children).find((node) => node.tagName === 'H1');
    if (heading && (!title || heading.textContent.trim().toLowerCase() === title.toLowerCase())) heading.remove();

    const paragraphs = Array.from(this.content.children).filter((node) => node.tagName === 'P');
    const category = paragraphs.find((node) => !/[0-9]|min|kcal|pieces|serves/i.test(node.textContent));
    const categoryTarget = this.querySelector('[data-recipe-category]');
    if (category && categoryTarget && category.textContent.trim().length < 45) {
      categoryTarget.textContent = category.textContent.trim();
      category.remove();
    }

    const meta = paragraphs.find((node) => /min|kcal|pieces|serves|prep|cook/i.test(node.textContent));
    const statsTarget = this.querySelector('[data-recipe-stats]');
    if (meta && statsTarget) {
      const values = meta.textContent
        .split(/[|\u23F1\uD83D\uDC65\uD83D\uDD25\u2022]/)
        .map((value) => value.trim())
        .filter(Boolean);
      values.forEach((value) => {
        const item = document.createElement('div');
        item.className = 'fit-recipe-detail__stat';
        item.textContent = value;
        statsTarget.appendChild(item);
      });
      meta.remove();
    }

    const image = this.content.querySelector('img');
    const mediaTarget = this.querySelector('[data-recipe-media]');
    if (image && mediaTarget) {
      const sourceWrapper = image.closest('p, figure');
      image.removeAttribute('width');
      image.removeAttribute('height');
      mediaTarget.replaceChildren(image);
      if (sourceWrapper && !sourceWrapper.textContent.trim() && !sourceWrapper.querySelector('img')) sourceWrapper.remove();
    }
  }

  prepareContent() {
    const nodes = Array.from(this.content.children).filter((node) => node.tagName !== 'HR');
    const fragment = document.createDocumentFragment();
    let card = null;

    nodes.forEach((node) => {
      if (node.tagName === 'H2') {
        card = document.createElement('section');
        card.className = 'fit-recipe-content-card';
        const label = node.textContent.trim().toLowerCase();
        if (/step|method|direction|preparation/.test(label)) card.classList.add('fit-recipe-content-card--steps');
        fragment.appendChild(card);
      }
      (card || fragment).appendChild(node);
    });

    this.content.replaceChildren(fragment);
  }
}

if (!customElements.get('recipe-library')) customElements.define('recipe-library', RecipeLibrary);
if (!customElements.get('recipe-detail')) customElements.define('recipe-detail', RecipeDetail);
